"use client";
import { hasWindow } from "@klarify/utilities";

type BeforeUnloadCancellableListener = (
	event: BeforeUnloadEvent,
) => string | null;

type BeforeUnloadNonCancellableListener = (event: BeforeUnloadEvent) => void;

class BeforeUnloadManager {
	private cancellables: Set<BeforeUnloadCancellableListener> = new Set();
	private nonCancellables: Set<BeforeUnloadNonCancellableListener> = new Set();
	constructor() {
		if (hasWindow()) {
			window.addEventListener("beforeunload", this.onBeforeUnload.bind(this));
		}
	}

	[Symbol.dispose]() {
		if (hasWindow()) {
			window.removeEventListener(
				"beforeunload",
				this.onBeforeUnload.bind(this),
			);
		}
	}

	private onBeforeUnload(event: BeforeUnloadEvent) {
		for (const listener of this.cancellables) {
			const message = listener(event);
			if (message) {
				event.preventDefault();
				event.returnValue = message;
				return message;
			}
		}

		for (const listener of this.nonCancellables) {
			listener(event);
		}

		return undefined;
	}

	addCancellableListener(listener: () => string | null) {
		this.cancellables.add(listener);
		return () => {
			this.cancellables.delete(listener);
		};
	}

	addNonCancellableListener(listener: () => void) {
		this.nonCancellables.add(listener);
		return () => {
			this.nonCancellables.delete(listener);
		};
	}
}

export const beforeUnloadManager = new BeforeUnloadManager();
